import { COOKIES } from '@bulknutrients/bulk-shared-lib/constants';
import type { LogOptions } from '@lib/logs';
import {
  addBreadcrumb,
  type Breadcrumb,
  captureException,
  captureMessage,
  flush,
  withScope,
} from '@sentry/nextjs';

/**
 * Send to sentry and our logs if on server
 * @param breadcrumb Sentry Breadcrumb
 */
export const logBreadcrumb = (
  breadcrumb: Breadcrumb,
  cookies?: Partial<{ [key: string]: string }>
) => {
  breadcrumb.data = {
    ...breadcrumb.data,
    sessionId: cookies?.[COOKIES.session],
    userUuid: cookies?.[COOKIES.tracking],
  };
  addBreadcrumb(breadcrumb); // send to sentry
  console.log(
    `${breadcrumb.message}
    ${JSON.stringify(breadcrumb.data)}
    Session ID: ${cookies?.[COOKIES.session]}
    User UUID: ${cookies?.[COOKIES.tracking]}`
  ); // send to our logs
};

/**
 * Used to send errors and messages to our internal logs and sentry
 * @param message either a string or an error
 */
export const log = ({ error, message, location, cookies }: LogOptions) => {
  const detail = error ?? message; // so we can use either error or message and it make sense
  const sessionId = cookies?.[COOKIES.session];
  const userUuid = cookies?.[COOKIES.tracking];

  withScope((scope) => {
    if (location) scope.setTag('location', location);
    if (sessionId) scope.setTag('session_id', sessionId);
    if (userUuid) scope.setUser({ id: userUuid });

    if (detail instanceof Error) {
      captureException(detail); // send to sentry
      console.error(
        `Encountered error, triggered by: ${location}. error: ${detail.message}
      Session ID: ${sessionId}
      User UUID: ${userUuid}`
      ); // send to our logs
    } else {
      const formattedDetails =
        typeof detail === 'string' ? detail : JSON.stringify(detail);
      captureMessage(formattedDetails, 'error'); // send to sentry
      console.log(
        `${formattedDetails} 
      Session ID: ${sessionId}
      User UUID: ${userUuid}`
      ); // send to our logs
    }
  });

  // Best-effort drain for serverless runtimes where process can freeze quickly.
  void flush(2000);
};
